import { fetchAnalyticsMetrics, fetchHighLevelMetrics } from 'api/queries';
import { FC, useEffect, useState } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';
import { useRestQuery } from 'utils/hooks/api/useRestQuery';
import { parseMetricDefinition, parseMetricValueTypeMap } from 'modules/productAnalytics/utils';
import {
  ANALYTICS_METRIC_DEFINITIONS_URL,
  HIGH_LEVEL_METRIC_DEFINITIONS_URL,
} from 'api/MetricDefinitionsAPI';
import { MetricDefinition, Metric } from '../types';
import { MetricsContext } from './MetricsContext';

export const MetricsContextProvider: FC<React.PropsWithChildren<{ page: string }>> = ({
  page,
  children,
}) => {
  const [highLevelMetrics, setHighLevelMetrics] = useState<Metric[]>([]);
  const [analyticsMetrics, setAnalyticsMetrics] = useState<Metric[]>([]);
  const [metricsLoading, setMetricsLoading] = useState<boolean>(false);
  const [analyticsMetricsValueTypes, setAnalyticsMetricsValueTypes] = useState<
    Record<string, string>
  >({});
  const [highLevelMetricsValueTypes, setHighLevelMetricsValueTypes] = useState<
    Record<string, string>
  >({});
  const [analyticsMetricDefinitions, setAnalyticsMetricDefinitions] = useState<
    Record<string, MetricDefinition>
  >({});
  const [highLevelMetricDefinitions, setHighLevelMetricDefinitions] = useState<
    Record<string, MetricDefinition>
  >({});

  const { data: analyticsMetricDefinitionsData } = useRestQuery(
    ANALYTICS_METRIC_DEFINITIONS_URL,
    {},
    { refetchOnWindowFocus: false },
  );
  const { data: highLevelMetricDefinitionsData } = useRestQuery(
    HIGH_LEVEL_METRIC_DEFINITIONS_URL,
    {},
    { refetchOnWindowFocus: false },
  );

  const analyticsMetricDefinitionsStr = JSON.stringify(analyticsMetricDefinitionsData);
  const highLevelMetricDefinitionsStr = JSON.stringify(highLevelMetricDefinitionsData);

  const { data: highLevelMetricsData, isLoading: highLevelMetricsLoading } = useGraphql(
    fetchHighLevelMetrics,
    {
      variables: { page },
      onCompleted: (resp) => {
        const highLevelMetricsResponse = resp?.highLevelMetrics;
        if (highLevelMetricsResponse) {
          setHighLevelMetrics(highLevelMetricsResponse);
          const valueTypesMap = parseMetricValueTypeMap(highLevelMetricsResponse);
          setHighLevelMetricsValueTypes(valueTypesMap);
        }
      },
    },
  );

  const { data: analyticsMetricsData, isLoading: analyticsMetricsLoading } = useGraphql(
    fetchAnalyticsMetrics,
    {
      variables: { page },
      onCompleted: (resp) => {
        const analyticsMetricsResponse = resp?.analyticsMetrics;
        if (analyticsMetricsResponse) {
          setAnalyticsMetrics(analyticsMetricsResponse);
          const valueTypesMap = parseMetricValueTypeMap(analyticsMetricsResponse);
          setAnalyticsMetricsValueTypes(valueTypesMap);
        }
      },
    },
  );

  const analyticsMetricsStr = JSON.stringify(analyticsMetricsData);
  const highLevelMetricsStr = JSON.stringify(highLevelMetricsData);

  useEffect(() => {
    setMetricsLoading(analyticsMetricsLoading || highLevelMetricsLoading);
  }, [analyticsMetricsLoading, highLevelMetricsLoading]);

  useEffect(() => {
    if (analyticsMetricsData?.analyticsMetrics?.length > 0 && analyticsMetricDefinitionsStr) {
      const metricDefinitions = parseMetricDefinition(
        analyticsMetricsData.analyticsMetrics,
        analyticsMetricDefinitionsData,
      );
      setAnalyticsMetricDefinitions(metricDefinitions);
    }
  }, [analyticsMetricDefinitionsStr, analyticsMetricsStr]);

  useEffect(() => {
    if (highLevelMetricsData?.highLevelMetrics?.length > 0 && highLevelMetricDefinitionsStr) {
      const metricDefinitions = parseMetricDefinition(
        highLevelMetricsData.highLevelMetrics,
        highLevelMetricDefinitionsData,
      );
      setHighLevelMetricDefinitions(metricDefinitions);
    }
  }, [highLevelMetricDefinitionsStr, highLevelMetricsStr]);

  return (
    <MetricsContext.Provider
      value={{
        highLevelMetrics,
        analyticsMetrics,
        metricsLoading,
        analyticsMetricsValueTypes,
        highLevelMetricsValueTypes,
        analyticsMetricDefinitions,
        highLevelMetricDefinitions,
      }}
    >
      {children}
    </MetricsContext.Provider>
  );
};
